import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import React, { useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { addPlaidBankAccount } from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import { getBidderId, getUserFirstName, getUserLastName } from '@/redux/modules/account/user/user.selectors';
import { getDeskSupportLink } from '@/utils/urls';

const onExit = (error, metadata) => {
    // log errors if any
    if (error) {
        console.error(error, metadata);
    }
};

type Props = {
    plaidLinkToken: string;
    useTextStyle?: boolean;
};

const LaPlaidLinkLink = ({ plaidLinkToken, useTextStyle }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const lastName = useAppSelector(getUserLastName);
    const firstName = useAppSelector(getUserFirstName);
    const bidderId = useAppSelector(getBidderId);

    const onSuccess = useCallback(
        (token, metadata) => {
            dispatch(
                addPlaidBankAccount({
                    accountId: metadata.account_id,
                    bidderId,
                    firstName,
                    lastName,
                    token,
                })
            );
        },
        [bidderId, dispatch, firstName, lastName]
    );

    const config = {
        onExit,
        onSuccess,
        token: plaidLinkToken,
    };

    const { error, open, ready } = usePlaidLink(config);

    return (
        <>
            {Boolean(error) && (
                <StyledError>
                    <BodySecondary color="critical">
                        <FormattedMessage
                            id="plaid.error"
                            values={{
                                link: (
                                    <Link
                                        key="plaid-error-contact-support-link"
                                        target="_blank"
                                        to={getDeskSupportLink()}
                                    >
                                        <FormattedMessage id="plaid.errorContactSupport" />
                                    </Link>
                                ),
                            }}
                        />
                    </BodySecondary>
                </StyledError>
            )}
            {useTextStyle ? (
                // @ts-expect-error onclick type issue
                <InlineButton onClick={open}>{formatMessage({ id: 'checkout.payment.addBankAccount' })}</InlineButton>
            ) : (
                <StyledButton
                    disabled={!ready || Boolean(error)}
                    // @ts-expect-error onclick type issue
                    onClick={open}
                >
                    {formatMessage({ id: 'plaid.addBank' })}
                </StyledButton>
            )}
        </>
    );
};

export default LaPlaidLinkLink;

const StyledError = styled.div`
    margin-bottom: 24px;
`;

const StyledButton = styled(Button)`
    margin-top: 8px;
`;
